import type * as ents from '@attain-apps/entities'

import type { Collection } from '../collection'

import type { Feature } from './feature'

/** Selectors exposed to the owning knowledge-base entity collection. */
export class Selectors {
  /** Document currently awaiting delete confirmation. */
  public readonly deletingFile

  /** Explanation shown before deleting the selected document. */
  public readonly deleteConfirmationDescription

  /** User-facing failure from the current deletion request. */
  public readonly deletionError

  /** Whether the confirmed document deletion is in progress. */
  public readonly isDeleting

  /** Whether a document's download URL is currently being requested. */
  public readonly isDownloading

  /** User-facing failure from one document download request. */
  public readonly downloadError

  /** Whether a document upload is currently being submitted. */
  public readonly isUploading

  /** User-facing failure from the current upload request. */
  public readonly uploadError

  constructor(feature: Feature, collection: Collection) {
    const deletion = feature.state.project(state => state.deletion)
    const downloads = feature.state.project(state => state.downloads)

    this.deletingFile = deletion.project(
      collection.plugins.cachePlugin.feature.selectors.lookup.asSelector,
      (operation, fileLookup) =>
        operation ? fileLookup[operation.fileKey] : undefined,
    )
    this.isDeleting = deletion.project(
      operation => operation?.entry?.loading === true,
    )
    this.deletionError = deletion.project(operation =>
      _errorMessage(operation?.entry?.error),
    )
    this.deleteConfirmationDescription = this.deletingFile.project(file =>
      file
        ? `Delete <b>${_escapeHtml(file.name)}</b>? Search results will update after knowledge-base indexing completes.`
        : '',
    )
    const downloadEntry = feature.createSelectorMethod(
      (key: ents.core.agent.KnowledgeBase.Management.Key) =>
        downloads.project(lookup => lookup[key]).asSelector,
    )
    this.downloadError = feature.createSelectorMethod(
      (key: ents.core.agent.KnowledgeBase.Management.Key) =>
        downloadEntry(key).project(entry => _errorMessage(entry?.error))
          .asSelector,
    )
    this.isDownloading = feature.createSelectorMethod(
      (key: ents.core.agent.KnowledgeBase.Management.Key) =>
        downloadEntry(key).project(entry => entry?.loading === true).asSelector,
    )
    this.isUploading = feature.state.project(
      state => state.upload?.loading === true,
    )
    this.uploadError = feature.state.project(state =>
      _errorMessage(state.upload?.error),
    )
  }
}

/** Converts an operation failure into a user-facing explanation. */
function _errorMessage(error: Error | undefined): string | undefined {
  if (error === undefined) {
    return undefined
  }
  return error.message || 'The knowledge base operation could not be completed.'
}

/** Escapes a document name before displaying it as confirmation markup. */
function _escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}
